import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import utils from '../../utils';
import { HiTrash } from 'react-icons/hi';
import adminServices from '../../services/AdminServices';
import { useAdminData } from '../../context/SessionContext';
import FormError from '../feedback/FormError';

function CategoryModal({ action, activeCategory, onReload, setActiveCategory }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isShift, setIsShift] = useState(false)
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { setCategories } = useAdminData();
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState()

    const tabs = useMemo(() => {
        const baseTabs = ['Info'];
        if (action === 1) {
            return [...baseTabs, 'Delete'];
        }
        return baseTabs;
    }, [action]);

    useEffect(() => {
        if (action === 1 && activeCategory) {
            setName(activeCategory.name || '');
            setDescription(activeCategory.description || '');
            setIsShift(activeCategory?.is_shift)
        } else {
            setName('');
            setDescription('');
            setIsShift(false)
        }

    }, [action, activeCategory]);

    const updateSession = () => {
        adminServices.getCategories().then(
            (res) => {
                setCategories(res.data || []);
            },
            (err) => {
                console.log('Session update error', err);
            }
        );
    };

    const handleSubmit = (e) => {
        setError()
        e.preventDefault();
        const closeBtn = document.getElementById('category-modal-close-btn');
        setLoading(true);

        const payload = { name, description, is_shift: isShift };

        let request;
        if (action === 1) {
            request = adminServices.updateCategory(activeCategory.id, payload);
        } else {
            request = adminServices.addCategory(payload);
        }

        request
            .then((res) => {
                const successMsg = action === 1 ? 'Category Updated Successfully' : 'Category Added Successfully';
                toast.success(successMsg);
                setLoading(false);
                onReload?.();
                updateSession();
                closeBtn?.click();
            })
            .catch((err) => {
                toast.error(utils.toastError);
                console.error(err);
                setError(err)
                setLoading(false);
            });
    };

    const handleDelete = () => {
        const closeBtn = document.getElementById('category-modal-close-btn');
        setDeleteLoading(true);

        adminServices.deleteCategory(activeCategory.id)
            .then((res) => {
                toast.success('Category Deleted Successfully');
                setDeleteLoading(false);
                onReload?.();
                updateSession();
                closeBtn?.click();
            })
            .catch((err) => {
                toast.error(utils.toastError);
                console.error(err);
                setDeleteLoading(false);
            });
    };

    const changeTab = (idx) => {
        if (action === 1) {
            setActiveTab(idx);
        }
    };

    function clearData() {
        setName('')
        setDescription('')
        setActiveTab(0)
        setIsShift(false)
        setActiveCategory(null)
        setError()
    }

    return (
        <div>
            <label htmlFor="category-modal" id="category-open-btn" className="hidden">
                Add / Update Category
            </label>
            <input type="checkbox" id="category-modal" className="modal-toggle" />

            <div className="modal">
                <div className="modal-box w-8/11 max-w-2xl">
                    <label
                        onClick={clearData}
                        htmlFor="category-modal"
                        id="category-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </label>

                    <h2 className="text-lg font-bold mb-3">
                        {action === 1 ? 'Edit' : 'Add'}
                        <span> Category{action === 1 && activeCategory && ':'}</span>
                        {action === 1 && activeCategory && (<span className='text-gray-500 ml-2'>{activeCategory?.name}</span>)}
                    </h2>

                    <div className="flex justify-center mb-6">
                        <div className="border-gray-200 dark:border-border-dark">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx
                                            ? 'tab-active bg-primary text-white rounded-lg'
                                            : ''
                                            }`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="">
                        {action === 1 && activeCategory && (
                            <>
                                <div className="border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-3 shadow-sm transition-all hover:shadow-md rounded-xl mb-6">
                                    <div className="space-y-2 text-sm">
                                        <p className='font-bold'>Overview</p>
                                        <p>
                                            <span className="font-bold mr-2">Services:</span>
                                            {activeCategory.service_count || 0}
                                        </p>
                                        {activeCategory.creation_time && (
                                            <p>
                                                <span className="font-bold mr-2">Created:</span>
                                                {new Date(activeCategory.creation_time).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>

                            </>
                        )}
                    </div>

                    {activeTab === 0 && (
                        <>
                            <form onSubmit={handleSubmit}>
                                {error && (<FormError error={error} />)}

                                <div className="form-control mb-3">
                                    <label className="label">
                                        <span className="label-text">Name *</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        className="input input-bordered w-full"
                                        placeholder="e.g. Eye Health"
                                    />
                                </div>

                                <div className="form-control mb-3">
                                    <label className="label">
                                        <span className="label-text">Description</span>
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="textarea textarea-bordered w-full"
                                        rows="3"
                                        placeholder="Briefly describe this category..."
                                    />
                                </div>

                                <div className="mb-3 flex gap-4 items-center">
                                    <label className="label">
                                        <span className="label-text">Is Shift Work</span>
                                    </label>
                                    <input
                                        type="checkbox"
                                        className="checkbox"
                                        checked={isShift}
                                        onChange={(e) => setIsShift(e.target.checked)}
                                    />
                                </div>

                                {/* Submit Button */}
                                <div className="mt-6">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                                    >
                                        {loading ? (
                                            <span>{action === 1 ? 'Updating...' : 'Adding...'}</span>
                                        ) : (
                                            <span>{action === 1 ? 'Update' : 'Add'}</span>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </>

                    )}

                    {activeTab === 1 && (
                        < div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden mt-6">
                            <div className="p-6 md:p-8">
                                <div className="border-l-4 border-error pl-4 py-2">
                                    <h3 className="font-bold text-error">Danger Zone</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-500 mt-2">
                                        Deleting this category is irreversible. All associated services will be removed.
                                    </p>
                                    <div className="pt-4">
                                        {deleteLoading ? (
                                            <div className="flex justify-center py-2">
                                                <span className="loading loading-spinner loading-sm"></span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleDelete}
                                                className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                                            >
                                                <HiTrash className="h-4 w-4" />
                                                <span>Delete: {activeCategory?.name}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
}

export default CategoryModal;